<script setup lang="ts" generic="T extends string | number">
/**
 * PSegment — groupe de boutons exclusif (toggle group / segmented control).
 *
 * Pattern : v-model standard. Le caller fournit une liste d'options
 * `{ value, label }` et reçoit la valeur sélectionnée via v-model. Le
 * design Revelio est neutre flat : bordure 1px line, segment actif
 * bg ink + text surface. Les segments inactifs sont en text-ink-4 hover ink.
 *
 * Cas d'usage : switch tenant (super_admin), filtre période (24h/7j/30j),
 * mode d'affichage (table/cards), connexion en tant que (cf. design
 * Revelio login).
 *
 * Note : pour 4+ options avec labels longs, préférer un PSelect (V1.5).
 */
interface Option<V> {
    value: V
    label: string
    /** Optional aria-label si label est une icône/abréviation. */
    ariaLabel?: string
    disabled?: boolean
}

interface Props {
    modelValue: T
    options: Option<T>[]
    size?: 'sm' | 'md'
    /** Si true, le segment prend toute la largeur dispo. */
    block?: boolean
}

withDefaults(defineProps<Props>(), {
    size: 'md',
    block: false,
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: T): void
}>()

function select(value: T): void {
    emit('update:modelValue', value)
}
</script>

<template>
    <div
        :class="['p-segment', `p-segment--${size}`, { 'p-segment--block': block }]"
        role="group"
    >
        <button
            v-for="opt in options"
            :key="String(opt.value)"
            type="button"
            :class="[
                'p-segment__item',
                { 'p-segment__item--active': modelValue === opt.value },
            ]"
            :aria-label="opt.ariaLabel"
            :aria-pressed="modelValue === opt.value ? 'true' : 'false'"
            :disabled="opt.disabled"
            @click="select(opt.value)"
        >
            {{ opt.label }}
        </button>
    </div>
</template>

<style scoped>
.p-segment {
    display: inline-flex;
    align-items: stretch;
    background: var(--p-color-surface);
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    overflow: hidden;
}

.p-segment--block {
    display: flex;
    width: 100%;
}

.p-segment__item {
    background: transparent;
    border: 0;
    border-right: 1px solid var(--p-color-line);
    color: var(--p-color-ink-4);
    cursor: pointer;
    font-family: var(--p-font-sans);
    font-weight: var(--p-font-weight-medium);
    transition:
        background var(--p-motion-fast) var(--p-ease-out),
        color var(--p-motion-fast) var(--p-ease-out);
}
.p-segment__item:last-child {
    border-right: 0;
}
.p-segment__item:hover:not(:disabled):not(.p-segment__item--active) {
    color: var(--p-color-ink);
    background: var(--p-color-surface-alt);
}
.p-segment__item:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.p-segment__item--active {
    background: var(--p-color-ink);
    color: var(--p-color-surface);
}

.p-segment--sm .p-segment__item {
    padding: 4px 12px;
    font-size: var(--p-text-xs);
    min-height: 24px;
}
.p-segment--md .p-segment__item {
    padding: 6px 14px;
    font-size: var(--p-text-sm);
    min-height: 32px;
}

.p-segment--block .p-segment__item {
    flex: 1;
}
</style>
